"use client";

import { useState, useEffect } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const AllBtobsalesCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    company_name: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    customer_state: "",
    GSTIN: "",
    billing_address: "",
  });

  /* ================= FETCH CUSTOMERS ================= */
  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get(
        "https://logicx.premierlam.com/api/get-pixel-btobsales-customers",
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
          },
        }
      );
      setCustomers(res.data.data || []);
    } catch {
      toast.error("Failed to fetch customers");
    } finally {
      setIsLoading(false);
    }
  };

  /* ================= ADD CUSTOMER ================= */
  const handleAddCustomer = async (e) => {
    e.preventDefault();

    if (newCustomer.customer_phone.length !== 10) {
      toast.error("Mobile number must be exactly 10 digits");
      return;
    }

    try {
      setIsSubmitting(true);

      await axios.post(
        "https://logicx.premierlam.com/api/add-pixel-btobsales-customers",
        newCustomer,
        {
          headers: {
            authorization: localStorage.getItem("accessToken"),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Customer added successfully!");
      setIsAddDialogOpen(false);
      setNewCustomer({
        company_name: "",
        customer_name: "",
        customer_phone: "",
        customer_email: "",
        customer_state: "",
        GSTIN: "",
        billing_address: "",
      });

      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add customer");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* ================= HELPERS ================= */
  const filteredCustomers = customers.filter((c) =>
    [
      c.company_name,
      c.customer_name,
      c.customer_email,
      c.customer_phone,
      c.customer_state,
      c.GSTIN,
      c.billing_address,
      c.added_by,
    ]
      .join(" ")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const d = new Date(dateString);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="min-h-screen bg-gray-50 p-2">
        <div className="max-w-7xl mx-auto">

          {/* HEADER */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-1">
                B2B Sales Customers
              </h1>
              <p className="text-gray-500">
                Manage B2B sales customers
              </p>
            </div>

            <button
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Add Customer
            </button>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-lg shadow-sm border p-6">

            {/* SEARCH */}
            <input
              type="text"
              placeholder="Search customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-4 px-4 py-2 border rounded-lg w-64"
            />

            <div className="overflow-x-auto">
              {isLoading ? (
                <div className="text-center py-6">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-gray-600">
                      <th className="py-3 px-4 text-left">Company Name</th>
                      <th className="py-3 px-4 text-left">Customer Name</th>
                      <th className="py-3 px-4 text-left">Email</th>
                      <th className="py-3 px-4 text-left">Phone</th>
                      <th className="py-3 px-4 text-left">State</th>
                      <th className="py-3 px-4 text-left">GSTIN</th>
                      <th className="py-3 px-4 text-left">Billing Address</th>
                      <th className="py-3 px-4 text-left">Created At</th>
                      <th className="py-3 px-4 text-left">Added By</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredCustomers.map((c) => (
                      <tr key={c.created_at} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{c.company_name}</td>
                        <td className="py-3 px-4">{c.customer_name}</td>
                        <td className="py-3 px-4">{c.customer_email}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {c.customer_phone}
                        </td>
                        <td className="py-3 px-4">{c.customer_state}</td>
                        <td className="py-3 px-4">{c.GSTIN}</td>
                        <td className="py-3 px-4">{c.billing_address}</td>
                        <td className="py-3 px-4 whitespace-nowrap">
                          {formatDate(c.created_at)}
                        </td>
                        <td className="py-3 px-4">{c.added_by}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ADD CUSTOMER MODAL */}
          {/* ADD CUSTOMER MODAL */}
          {isAddDialogOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
              <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-2xl relative">
                <button
                  className="absolute top-2 right-2 text-gray-400"
                  onClick={() => setIsAddDialogOpen(false)}
                >
                  ✖
                </button>

                <h2 className="text-lg font-semibold mb-4">
                  Add B2B Sales Customer
                </h2>

                <form
                  onSubmit={handleAddCustomer}
                  className="grid grid-cols-2 gap-4"
                >
                  {/* COMPANY NAME */}
                  <div>
                    <label className="text-sm font-medium">Company Name</label>
                    <input
                      type="text"
                      value={newCustomer.company_name}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, company_name: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* CUSTOMER NAME */}
                  <div>
                    <label className="text-sm font-medium">Customer Name</label>
                    <input
                      type="text"
                      value={newCustomer.customer_name}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, customer_name: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* EMAIL */}
                  <div>
                    <label className="text-sm font-medium">Customer Email</label>
                    <input
                      type="email"
                      value={newCustomer.customer_email}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, customer_email: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* PHONE */}
                  <div>
                    <label className="text-sm font-medium">
                      Customer Phone (10 digits)
                    </label>
                    <input
                      type="text"
                      value={newCustomer.customer_phone}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (/^\d{0,10}$/.test(value)) {
                          setNewCustomer({ ...newCustomer, customer_phone: value });
                        }
                      }}
                      onBlur={() => {
                        if (newCustomer.customer_phone.length !== 10) {
                          toast.error("Mobile number must be 10 digits");
                        }
                      }}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* STATE */}
                  <div>
                    <label className="text-sm font-medium">Customer State</label>
                    <input
                      type="text"
                      value={newCustomer.customer_state}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, customer_state: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* GSTIN */}
                  <div>
                    <label className="text-sm font-medium">GSTIN</label>
                    <input
                      type="text"
                      value={newCustomer.GSTIN}
                      onChange={(e) =>
                        setNewCustomer({ ...newCustomer, GSTIN: e.target.value })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* BILLING ADDRESS */}
                  <div className="col-span-2">
                    <label className="text-sm font-medium">Billing Address</label>
                    <textarea
                      rows="3"
                      value={newCustomer.billing_address}
                      onChange={(e) =>
                        setNewCustomer({
                          ...newCustomer,
                          billing_address: e.target.value,
                        })
                      }
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>

                  {/* ACTIONS */}
                  <div className="col-span-2 flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsAddDialogOpen(false)}
                      className="px-4 py-2 bg-gray-100 rounded"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-orange-500 text-white rounded"
                    >
                      {isSubmitting ? "Adding..." : "Add Customer"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}


        </div>
      </div>
    </>
  );
};

export default AllBtobsalesCustomers;
